import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageSquare, Plus, FileText, Clock, Users } from 'lucide-react';
import ChatLayout from '@/components/chat/layout/ChatLayout';
import { ChatInterfaceProvider } from '@/contexts/ChatInterfaceContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

const ChatStarter = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Fetch user's posits
  const { data: userPosits = [], isLoading } = useQuery({
    queryKey: ['chat-starter-posits', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('posits')
        .select('id, title, summary_light, privacy, updated_at')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) {
        return [];
      }

      return data;
    },
    enabled: !!user?.id,
  });

  // Fetch recent chat sessions
  const { data: recentChats = [] } = useQuery({
    queryKey: ['recent-chat-sessions', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('chat_history')
        .select(`
          id,
          posit_id,
          last_message_at,
          posits!inner (
            id,
            title
          )
        `)
        .eq('user_id', user.id)
        .order('last_message_at', { ascending: false })
        .limit(5);

      if (error) {
        return [];
      }

      return data;
    },
    enabled: !!user?.id,
  });

  const handlePositChat = (positId: string) => {
    navigate(`/chat/${positId}`);
  };

  const handleResumeChat = (positId: string, chatId: string) => {
    navigate(`/chat/${positId}/${chatId}`);
  };

  return (
    <div className="h-screen w-screen bg-background relative overflow-hidden">
      <ChatInterfaceProvider>
        <ChatLayout>
          <div className="flex-1 flex flex-col h-full overflow-hidden">
            <div className="flex-1 p-6 space-y-6">
              {/* Header */}
              <div className="space-y-2">
                <h1 className="text-3xl font-bold tracking-tight">Start a Chat</h1>
                <p className="text-muted-foreground text-lg">
                  Choose a Posit to chat with or continue a previous conversation.
                </p>
              </div>

              <div className="grid gap-6 lg:grid-cols-2">
                {/* Posits */}
                <button
                  onClick={() => navigate('/dashboard/posits')}
                  className="flex items-center gap-3 w-full p-3 text-left hover:bg-gray-50 rounded-lg transition-colors group"
                >
                  Posits
                </button>

                {/* Recent Conversations */}
                <Card className="flex flex-col">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      Recent Conversations
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1">
                    {recentChats.length === 0 ? (
                      <div className="text-center py-8">
                        <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground text-sm">
                          No recent conversations. Start chatting with your Posits!
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {recentChats.map(chat => (
                          <div 
                            key={chat.id}
                            className="p-4 border border-border rounded-lg hover:border-primary/50 transition-colors cursor-pointer"
                            onClick={() => handleResumeChat(chat.posit_id, chat.id)}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <h3 className="font-medium text-sm">{chat.posits.title}</h3>
                              <MessageSquare className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(chat.last_message_at), 'MMM d, h:mm a')}
                              </span>
                              <Button size="sm" variant="ghost" className="h-6 px-2">
                                Resume
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={() => navigate('/dashboard/create')}
                  variant="outline"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create New Posit
                </Button>
                <Button 
                  onClick={() => navigate('/dashboard/posits')}
                  variant="outline"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Manage Posits
                </Button>
                <Button 
                  onClick={() => navigate('/public-posits')}
                  variant="outline"
                >
                  <Users className="mr-2 h-4 w-4" />
                  Browse Public Posits
                </Button>
              </div>
            </div>
          </div>
        </ChatLayout>
      </ChatInterfaceProvider>
    </div>
  );
};

export default ChatStarter; 